import {
  DefaultChatTransport,
  type ChatTransport,
  type UIMessage,
  type UIMessageChunk,
} from 'ai';

export type CyrusTransportOptions = {
  sessionId?: string | null;
  initialEventId?: number;
  provider: () => string;
  headers: () => Record<string, string>;
  onSession: (sessionId: string) => void;
  onChunk?: (chunk: UIMessageChunk) => void;
};

/**
 * AI SDK UI transport for the Cyrus Agents SDK backend.
 *
 * The delegate owns AI SDK's validated SSE decoding. This wrapper adds Cyrus
 * session creation/reconnection semantics without moving inference or provider
 * credentials into the browser.
 */
export class CyrusChatTransport implements ChatTransport<UIMessage> {
  private sessionId: string | null;
  private lastEventId: number;
  private readonly delegate: DefaultChatTransport<UIMessage>;

  constructor(private readonly options: CyrusTransportOptions) {
    this.sessionId = options.sessionId ?? null;
    this.lastEventId = options.initialEventId ?? 0;
    this.delegate = new DefaultChatTransport<UIMessage>({
      api: '/api/chat',
      headers: () => this.options.headers(),
      fetch: (async (input, init) => {
        const response = await globalThis.fetch(input, init);
        const established = response.headers.get('x-cyrus-session-id');
        if (established && established !== this.sessionId) {
          this.sessionId = established;
          this.options.onSession(established);
        }
        return response;
      }) as typeof globalThis.fetch,
      prepareSendMessagesRequest: ({ id, messages, trigger, messageId, body, headers }) => ({
        api: '/api/chat',
        ...(headers ? { headers } : {}),
        body: {
          ...body,
          id,
          // Cyrus persists and reconstructs prior messages server-side. Sending the
          // full hydrated UI transcript eventually exceeds the HTTP body limit and
          // duplicates history at the transport boundary.
          messages: messages.slice(-1),
          trigger,
          messageId,
          sessionId: this.sessionId,
          afterEventId: this.lastEventId,
          provider: this.options.provider(),
        },
      }),
      prepareReconnectToStreamRequest: ({ headers }) => ({
        api: this.sessionId
          ? `/api/chat/${encodeURIComponent(this.sessionId)}/stream?after=${this.lastEventId}`
          : '/api/chat/unavailable/stream',
        ...(headers ? { headers } : {}),
      }),
    });
  }

  currentSessionId(): string | null {
    return this.sessionId;
  }

  async sendMessages(
    options: Parameters<ChatTransport<UIMessage>['sendMessages']>[0],
  ): Promise<ReadableStream<UIMessageChunk>> {
    return this.observe(await this.delegate.sendMessages(options));
  }

  async reconnectToStream(
    options: Parameters<ChatTransport<UIMessage>['reconnectToStream']>[0],
  ): Promise<ReadableStream<UIMessageChunk> | null> {
    if (!this.sessionId) return null;
    const stream = await this.delegate.reconnectToStream(options);
    return stream ? this.observe(stream) : null;
  }

  async interrupt(): Promise<void> {
    if (!this.sessionId) return;
    await fetch(`/api/sessions/${encodeURIComponent(this.sessionId)}/interrupt-turn`, {
      method: 'POST',
      headers: this.options.headers(),
    });
  }

  private observe(stream: ReadableStream<UIMessageChunk>): ReadableStream<UIMessageChunk> {
    return stream.pipeThrough(new TransformStream<UIMessageChunk, UIMessageChunk>({
      transform: (chunk, controller) => {
        if (chunk.type === 'data-cyrus-event' && isEventCursor(chunk.data)) {
          this.lastEventId = Math.max(this.lastEventId, chunk.data.eventId);
        }
        this.options.onChunk?.(chunk);
        controller.enqueue(chunk);
      },
    }));
  }
}

function isEventCursor(value: unknown): value is { eventId: number } {
  return typeof value === 'object'
    && value !== null
    && 'eventId' in value
    && typeof value.eventId === 'number';
}
